<svelte:options
  customElement={{
    tag: "oc-accordion-v1",
    extend: window.__components.extend(),
    props: {
      title: { type: "String" },
      expanded: { type: "Boolean", reflect: true },
      hideDivider: { type: "Boolean", attribute: "hide-divider" },
    },
  }}
/>

<script lang="ts">
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import {
    useDomAnimationHelper,
    OnAnimationFrame,
    onElementAnimated,
    OnImmediate,
  } from "@otto-ec/otto-components-utils/svelte/useDomAnimationHelper";
  import type { Events, Props } from "./AccordionV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let { title = "", expanded = false, hideDivider = false }: Props = $props();

  const Host = $host();
  const dispatch = useEventDispatcher<Events>(Host);

  let contentElement = $state<HTMLElement>();
  const [expandState, setExpanded] = useDomAnimationHelper({
    expanded: [OnImmediate, onElementAnimated(() => contentElement)],
    notClosed: [OnAnimationFrame, OnImmediate],
  });

  /*                                          */
  $effect(() => setExpanded(expanded));

  function onClick(ev: Event): void {
    ev.preventDefault();

    /*                                             */
    /*                                                     */
    if (
      !dispatch(!expanded ? "oc-accordion-open" : "oc-accordion-close", undefined, {
        alias: [!expanded ? "oc-open" : "oc-close"],
      })
    ) {
      return;
    }

    /*                                                        */
    /*                                   */
    expanded = !expanded;
  }
</script>

<div id="panel-title">
  {#if !hideDivider}
    <oc-divider-v1></oc-divider-v1>
  {/if}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <oc-row-v2
    class="trigger"
    as-button={true}
    onclick={onClick}
    class:oc-row-v1--hide-divider={hideDivider}
    oc-aria-expanded={expandState.expanded}
    aria-controls="content"
    hide-divider={true}
  >
    <div class="title">
      <slot name="title">
        {title}
      </slot>

      <oc-icon-v1 type="arrow-down" size={/*                      */ "100"}></oc-icon-v1>
    </div>
  </oc-row-v2>
</div>
<div
  bind:this={contentElement}
  class="content"
  class:closed={!expandState.notClosed}
  role="region"
  aria-labelledby="panel-title"
  id="content"
  hidden={!expandState.expanded}
>
  <slot />
</div>

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;

  :global {
    .trigger {
      > * {
        grid-area: content;
      }

      & > .title {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
      }

      & slot[name="title"] {
        display: block;
        font: tokens.$oc-component-accordion-label-font;
        color: tokens.$oc-component-accordion-label-color;
      }

      & oc-icon-v1 {
        --color: #{tokens.$oc-component-accordion-icon-color};
        flex: 0 0 auto;
        /*                                                                */
        transition: transform 100ms ease-out;
      }

      &[oc-aria-expanded="true"] oc-icon-v1 {
        transform: rotate(-180deg);
      }
    }

    .content {
      display: grid;
      grid-template-rows: 1fr;
      padding: tokens.$oc-component-accordion-gap-y 0 tokens.$oc-component-accordion-spacing-bottom
        0;
      /*                                                                */
      transition: grid-template-rows 100ms ease-out;

      @media (prefers-reduced-motion) {
        transition: none;
      }
    }

    .content.closed {
      padding: 0;
      grid-template-rows: 0fr;
    }

    [hidden] {
      display: none;
    }

    .content > slot {
      display: block;
      overflow: hidden;
    }
  }
</style>
